/* KAIROS 2026-10-09: load recent cards only when their section is nearby.
 * Preserves the bounded, inertly validated 2026-09-26 product-card loader.
 * Requires templates/product.card.liquid from this fix.
 * Independent of CardProductSlider: it must not initialize before cards exist.
 */
(() => {
  'use strict';
  if (customElements.get('recently-viewed-products')) return;

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.js-section');
      this.wrapper = this.querySelector('.swiper-wrapper');
      if (!this.section || !this.wrapper || this.run) return;
      if (this.loaded) {
        this.finish();
        return;
      }
      this.section.classList.add('hidden');
      this.wrapper.replaceChildren();
      // An empty history needs neither requests nor a visibility observer.
      if (!this.handles().length) {
        this.loaded = true;
        return;
      }
      const run = { controller: new AbortController() };
      this.run = run;
      this.waitUntilNearSection(run);
    }

    disconnectedCallback() {
      if (this.run) {
        this.clearVisibilityWait(this.run);
        this.run.controller.abort();
        clearTimeout(this.run.deadline);
        clearTimeout(this.run.requestTimer);
      }
      this.run = null;
      if (this.slider && !this.slider.destroyed) this.slider.destroy(true, true);
      this.slider = null;
      if (this.onFocus) this.wrapper?.removeEventListener('focusin', this.onFocus);
      this.onFocus = null;
    }

    clearVisibilityWait(run) {
      run.observer?.disconnect();
      run.observer = null;
      run.marker?.remove();
      run.marker = null;
    }

    waitUntilNearSection(run) {
      const start = () => {
        if (this.run !== run || !this.isConnected || run.controller.signal.aborted) return;
        this.clearVisibilityWait(run);
        this.load(run);
      };
      // Keep the editor and browsers without IntersectionObserver functional.
      if (window.Shopify?.designMode || typeof window.IntersectionObserver !== 'function') {
        start();
        return;
      }
      try {
        // The section is display:none until cards exist. Observe a tiny marker
        // OUTSIDE it, not the hidden section. Net vertical space remains zero.
        run.marker = document.createElement('span');
        run.marker.setAttribute('aria-hidden', 'true');
        run.marker.setAttribute('data-kairos-recent-trigger', '');
        run.marker.style.cssText = 'display:block;width:1px;height:1px;margin:0 0 -1px; padding:0;border:0;opacity:0;pointer-events:none;';
        this.section.before(run.marker);
        run.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) start();
        }, { rootMargin: '600px 0px', threshold: 0 });
        run.observer.observe(run.marker);
      } catch (_) {
        // Progressive enhancement: preserve the previous loading behaviour.
        start();
      }
    }

    handles() {
      try {
        const stored = (localStorage.getItem('recently-viewed') || '').slice(0, 16384);
        const limit = Math.min(12, Math.max(1, Number(this.dataset.maxProducts) || 8));
        return [...new Set(stored.split(',').map(value => value.trim()))]
          .filter(handle => handle !== this.dataset.productHandle && handle.length <= 255 &&
            /^[\p{L}\p{N}][\p{L}\p{N}_-]*$/u.test(handle))
          .slice(0, limit);
      } catch (_) {
        return [];
      }
    }

    cardFromHTML(html, handle) {
      // Check BEFORE parsing/upgrading any custom element. Full-page fallback is fatal.
      if (html.length > 120000 || /<(?:!doctype|html|head|body|main|product-selector|recently-viewed-products)(?:\s|>)/i.test(html)) {
        throw new Error('Expected a product card, received a page.');
      }
      // Template contents are inert: constructors, scripts and images cannot run here.
      const template = document.createElement('template');
      template.innerHTML = html;
      const envelope = template.content.firstElementChild;
      if (template.content.childElementCount !== 1 || !envelope?.matches('[data-kairos-product-card="v2"]') ||
          envelope.dataset.productHandle !== handle) {
        throw new Error('Missing or mismatched product-card marker.');
      }
      const cards = envelope.querySelectorAll('card-product');
      const card = cards[0];
      if (cards.length !== 1 || !card.matches('.card-product.disabled') || card.dataset.handle !== handle ||
          envelope.querySelector('script, iframe, object, embed, link, style, form, main, template')) {
        throw new Error('Unexpected product-card contents.');
      }
      // Only the theme card is allowed to become an active custom element.
      for (const node of envelope.querySelectorAll('*')) {
        if (node.localName.includes('-') && node.localName !== 'card-product') {
          throw new Error('Unexpected nested component in product card.');
        }
        for (const attribute of [...node.attributes]) {
          if (/^on/i.test(attribute.name)) node.removeAttribute(attribute.name);
        }
      }
      if (this.dataset.showUnavailable !== 'true' && envelope.dataset.productAvailable !== 'true') return null;
      return document.importNode(card, true);
    }

    async load(run) {
      const cards = [];
      // One request at a time, at most 12 requests, 12 seconds for the entire batch.
      run.deadline = setTimeout(() => run.controller.abort(), 12000);
      try {
        const root = window.Shopify?.routes?.root || '/';
        for (const handle of this.handles()) {
          if (this.run !== run || !this.isConnected || run.controller.signal.aborted) break;
          run.requestTimer = setTimeout(() => run.controller.abort(), 6000);
          try {
            const response = await fetch(`${root}products/${encodeURIComponent(handle)}?view=card`, {
              signal: run.controller.signal,
              credentials: 'same-origin',
              headers: { Accept: 'text/html' }
            });
            if (response.status === 404 || response.status === 410) continue;
            // Stop on rate limiting or server errors; do not keep hammering the store.
            if (!response.ok) break;
            if (Number(response.headers.get('content-length')) > 120000) break;
            const html = await response.text();
            if (this.run !== run || !this.isConnected || run.controller.signal.aborted) break;
            const card = this.cardFromHTML(html, handle);
            if (card) cards.push(card);
          } catch (error) {
            if (!run.controller.signal.aborted) {
              console.warn('[Kairos] Son goruntulenenler guvenli bicimde durduruldu:', error.message);
            }
            break;
          } finally {
            clearTimeout(run.requestTimer);
          }
        }
      } finally {
        clearTimeout(run.deadline);
        clearTimeout(run.requestTimer);
        if (this.run !== run || !this.isConnected) return;
        this.run = null;
        const fragment = document.createDocumentFragment();
        cards.forEach(card => {
          const slide = document.createElement('div');
          slide.className = 'swiper-slide card-product-slider__slide';
          slide.append(card);
          fragment.append(slide);
        });
        this.wrapper.replaceChildren(fragment);
        this.loaded = true;
        this.finish();
      }
    }

    finish() {
      if (!this.wrapper.children.length) {
        this.section.classList.add('hidden');
        return;
      }
      this.section.classList.remove('hidden');
      if (this.slider && !this.slider.destroyed) return;
      this.wrapper.id = `recently-viewed-${this.dataset.sectionId || 'products'}`;
      this.wrapper.setAttribute('aria-live', 'polite');
      [...this.wrapper.children].forEach((slide, index) => {
        slide.dataset.index = index;
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-label', `${index + 1} / ${this.wrapper.children.length}`);
      });
      const prevEl = this.querySelector('.swiper-button--prev');
      const nextEl = this.querySelector('.swiper-button--next');
      [prevEl, nextEl].forEach((button, index) => {
        if (!button) return;
        button.type = 'button';
        button.setAttribute('aria-controls', this.wrapper.id);
        button.setAttribute('aria-label', index ? 'Sonraki \u00fcr\u00fcnler' : '\u00d6nceki \u00fcr\u00fcnler');
      });
      let spacing = {};
      try { spacing = JSON.parse(this.dataset.swiperOptions || '{}'); } catch (_) { /* Use defaults. */ }
      if (typeof window.Swiper !== 'function') {
        // Readable horizontal cards even if the optional slider library cannot load.
        this.wrapper.style.cssText = 'display:grid;grid-auto-flow:column;grid-auto-columns:min(72vw,260px);gap:16px;overflow-x:auto;';
        [...this.wrapper.children].forEach(slide => { slide.style.width = '100%'; });
        [prevEl, nextEl].forEach(button => { if (button) button.hidden = true; });
        return;
      }
      this.slider = new window.Swiper(this, {
        a11y: false,
        threshold: 3,
        slidesPerView: 'auto',
        spaceBetween: Number(spacing.spaceBetweenMobile) || 2,
        resistanceRatio: 0.72,
        watchOverflow: true,
        navigation: { prevEl, nextEl },
        breakpoints: {
          750: { slidesPerView: 3, spaceBetween: Number(spacing.spaceBetweenDesktop) || 2 },
          1100: { slidesPerView: 4, spaceBetween: Number(spacing.spaceBetweenDesktop) || 2 }
        }
      });
      this.onFocus = event => {
        const slide = event.target.closest('.swiper-slide');
        if (slide && this.wrapper.contains(slide) && this.slider && !this.slider.destroyed) {
          this.scrollLeft = 0;
          this.slider.slideTo(Number(slide.dataset.index), 0);
        }
      };
      this.wrapper.addEventListener('focusin', this.onFocus);
    }
  }

  customElements.define('recently-viewed-products', RecentlyViewed);
})();
